import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaAngleDown, FaSignOutAlt, FaTimes } from 'react-icons/fa';

import { Fundo, Gaveta, Item, Rodape, Rolagem, Secao, SubMenu, TituloSecao, Topo } from './styles';
import { BotaoIcone } from '../Header/styles';

import UsuarioInfo from '../UsuarioInfo';
import IconeDinamico from '../IconeDinamico';
import useRotaBase from '../../utils/useRotaBase';
import { iconeMenu, temFilhos } from '../../utils/menu';
import { IMenSis } from '../../types/MenSis';

interface MenuMobileProps {
  aberto: boolean;
  onFechar: () => void;
  menuSistema: IMenSis[];
  atalhos: IMenSis[];
  onSair: () => void;
}

// Menu lateral do celular: fundo escurecido, atalhos, menu do sistema (3 níveis) e sair
export default function MenuMobile({ aberto, onFechar, menuSistema, atalhos, onSair }: MenuMobileProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const rotaBase = useRotaBase();

  const [expandidos, setExpandidos] = useState<Record<string, boolean>>({});

  function alternar(chave: string) {
    setExpandidos(prev => ({ ...prev, [chave]: !prev[chave] }));
  }

  function rotaAtiva(item: IMenSis) {
    return !!item.rot && location.pathname === `${rotaBase}${item.rot}`;
  }

  function clicar(item: IMenSis, chave: string) {
    if (temFilhos(item)) {
      alternar(chave);
      return;
    }

    if (item.rot) navigate(`${rotaBase}${item.rot}`);
    onFechar();
  }

  // Fecha ao trocar de rota (inclui o voltar do navegador)
  useEffect(() => {
    onFechar();
  }, [location.pathname]);

  // Esc fecha e o fundo da página não rola com o menu aberto
  useEffect(() => {
    if (!aberto) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onFechar();
    }

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = overflowAnterior;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [aberto]);

  function renderItens(itens: IMenSis[], nivel: number, prefixo: string): React.ReactNode {
    return itens.map((item, index) => {
      const chave = `${prefixo}${index}`;
      const comFilhos = temFilhos(item);

      return (
        <div key={chave}>
          <Item
            type='button'
            $nivel={nivel}
            $ativo={rotaAtiva(item)}
            aria-expanded={comFilhos ? !!expandidos[chave] : undefined}
            onClick={() => clicar(item, chave)}
          >
            <IconeDinamico nome={iconeMenu(item)} size={16} />
            <span>{item.mensis}</span>
            {comFilhos && <FaAngleDown className='seta' />}
          </Item>
          {comFilhos && (
            <SubMenu $aberto={!!expandidos[chave]}>
              {renderItens(item.filhos!, nivel + 1, `${chave}-`)}
            </SubMenu>
          )}
        </div>
      );
    });
  }

  return createPortal(
    <>
      <Fundo $aberto={aberto} onClick={onFechar} aria-hidden />
      <Gaveta $aberto={aberto} aria-label='Menu' aria-hidden={!aberto}>
        <Topo>
          <UsuarioInfo />
          <BotaoIcone type='button' onClick={onFechar} aria-label='Fechar menu'>
            <FaTimes size={20} />
          </BotaoIcone>
        </Topo>

        <Rolagem>
          {atalhos.length > 0 && (
            <Secao>
              <TituloSecao>Atalhos</TituloSecao>
              {renderItens(atalhos, 0, 'a')}
            </Secao>
          )}
          {menuSistema.length > 0 && (
            <Secao>
              <TituloSecao>Menu</TituloSecao>
              {renderItens(menuSistema, 0, 'm')}
            </Secao>
          )}
        </Rolagem>

        <Rodape>
          <Item type='button' $nivel={0} onClick={onSair}>
            <FaSignOutAlt size={16} />
            <span>Sair</span>
          </Item>
        </Rodape>
      </Gaveta>
    </>,
    document.body
  );
}
